// App, Page, Navigation — app.json

import type { UiElement } from "./element.ts";
import type { VariableDef } from "./state.ts";
import type { DataSource } from "./data.ts";
import type { PageEditorMeta } from "./editor.ts";

// ---- Block Layout (per-block layout metadata in Page) ----

export interface BlockLayout {
  width: "full" | "constrained" | "narrow" | string;
  /** Tailwind margin-top class for gap above this block (LLM-generated). */
  gap?: string;
  position?: "fixed-bottom" | "fixed-top" | "absolute" | "sticky" | string;
  /** @deprecated Use gap instead. */
  spacing?: string;
}

// ---- Block Position (per-block placement in Page — v57.1) ----

export interface AbsoluteBlockPosition {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FlowBlockPosition {
  order: number;
  width: "full" | "constrained" | "narrow" | string;
  /** Tailwind margin-top class for gap above this block. */
  gap?: string;
}

export type BlockPosition = AbsoluteBlockPosition | FlowBlockPosition;

// ---- Canvas (absolute layout page size — v57.1) ----

export interface CanvasSize {
  w: number;
  h: number;
}

// ---- Type Guards (v57.1) ----

export function isAbsolutePosition(pos: BlockPosition): pos is AbsoluteBlockPosition {
  return "x" in pos && "y" in pos;
}

export function isFlowPosition(pos: BlockPosition): pos is FlowBlockPosition {
  return "order" in pos;
}

/** Extract ordered block names from either legacy blocks[] or blockPositions. */
export function getBlockOrder(page: Page): string[] {
  if (page.blockPositions && page.layout === "flow") {
    return Object.entries(page.blockPositions)
      .filter(([, pos]) => isFlowPosition(pos))
      .sort(([, a], [, b]) => (a as FlowBlockPosition).order - (b as FlowBlockPosition).order)
      .map(([name]) => name);
  }
  if (page.blockPositions && page.layout === "absolute") {
    return Object.keys(page.blockPositions);
  }
  return page.blocks;
}

// ---- Navigation ----

export interface NavigationGuard {
  pattern: string;
  flow: string;
  timeout?: number;
}

export interface Navigation {
  defaultRoute: string;
  guards?: NavigationGuard[];
}

// ---- Page ----

export interface Page {
  id: string;
  route: string;
  name?: string;
  title?: string;
  description?: string;
  layout: "stack" | "sidebar" | "split" | "absolute" | "flow" | string; // v2: REQUIRED
  blocks: string[];
  roles?: string[];
  blockLayout: Record<string, BlockLayout>; // v2: REQUIRED
  /** Canvas size for absolute layout mode (v57.1). */
  canvas?: CanvasSize;
  /** Structured block positions for absolute/flow modes (v57.1). Replaces blockLayout for new pages. */
  blockPositions?: Record<string, BlockPosition>;
  _editor?: PageEditorMeta;  // editor-only metadata, stripped at build time
}

// ---- App ----

export interface App {
  appId: string;
  name: string;
  description?: string;
  version: string;
  schemaVersion: "3.0"; // v2 identifier
  theme?: string;
  themePath?: string;
  themes?: Record<string, { path: string; label: string }>;
  /** Component style preset — determines which cn-* CSS rules apply.
   *  Maps to `.style-{value}` CSS scope class (e.g. "luma" → ".style-luma").
   *  Default: "luma". LLM should set this in app.json. */
  style?: "luma" | "lyra" | "mira" | "vega" | "maia" | string;
  navigation?: Navigation;
  pages: Page[];
  globalBlocks?: string[];
  datasources?: Record<string, DataSource>;
  globalState: Record<string, VariableDef>;
  appData?: Record<string, unknown>;
  layouts?: Record<string, UiElement>;
  wallet?: { baseUrl: string; apiKey?: string; jwt?: string };
  tier?: 'A' | 'B';
  complexityScore?: number;
}
